import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import { getAllSettings, updateSettings } from "../../store/actions/settings";
import { useDispatch, useSelector } from "react-redux";
import { Switch } from "antd";

const ModernSettingsPage = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: "",
    mobile: "",
    emailPassKey: "",
    renewReferral: "",
    ewayRatePerPcs: 0,
    eInvoiceRatePerPcs: 0,
    terms: "",
    policy: "",
    refundPolicy: "",
    address: "",
    wpAuthToken: "",
    compName: "",
    gstNo: "",
    gstPer: 0,
    sac: "",
  });

  const settings = useSelector((state) => state.settings.list);

  const setting = useMemo(() => {
    return settings[0];
  }, [settings]);


  const [activeTab, setActiveTab] = useState("basic");

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  // Color scheme
  const colors = {
    primary: "#6366f1", // Indigo
    primaryLight: "#a5b4fc",
    secondary: "#f1f5f9", // Light gray
    accent: "#10b981", // Emerald
    danger: "#ef4444", // Red
    text: "#1e293b", // Dark slate
    textLight: "#64748b", // Slate
    background: "#f8fafc", // Very light gray
    white: "#ffffff",
    border: "#e2e8f0",
  };

  // Styles
  const containerStyle = {
    minHeight: "100vh",
    backgroundColor: colors.background,
    padding: "24px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const headerStyle = {
    color: colors.text,
    fontSize: "32px",
    fontWeight: "700",
    marginBottom: "8px",
    marginTop: "100px",
    letterSpacing: "-0.025em",
  };

  const subtitleStyle = {
    color: colors.textLight,
    fontSize: "16px",
    marginBottom: "32px",
  };

  const tabContainerStyle = {
    display: "flex",
    gap: "4px",
    marginBottom: "32px",
    backgroundColor: colors.white,
    padding: "4px",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  };

  const tabStyle = (isActive) => ({
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: isActive ? colors.primary : "transparent",
    color: isActive ? colors.white : colors.textLight,
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    flex: 1,
    textAlign: "center",
  });

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "16px",
    padding: "32px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${colors.border}`,
  };

  const cardHeaderStyle = {
    fontSize: "24px",
    fontWeight: "600",
    color: colors.text,
    marginBottom: "24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "24px",
    marginBottom: "24px",
  };

  const inputGroupStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };
  const inputGroupStyle2 = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    // grid-column: 1 / -1;
    gridColumn: "span 2",
  };
  const inputGroupStyle3 = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    // grid-column: 1 / -1;
    gridColumn: "span 4",
  };

  const labelStyle = {
    fontSize: "14px",
    fontWeight: "500",
    color: colors.text,
    marginBottom: "4px",
  };

  const inputStyle = {
    padding: "12px 16px",
    border: `2px solid ${colors.border}`,
    borderRadius: "8px",
    fontSize: "14px",
    backgroundColor: colors.white,
    color: colors.text,
    transition: "all 0.2s ease",
    outline: "none",
  };

  const buttonStyle = (variant = "primary") => ({
    padding: "12px 32px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    backgroundColor: variant === "primary" ? colors.primary : colors.accent,
    color: colors.white,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  });

  const textareaStyle = {
    ...inputStyle,
    minHeight: "120px",
    resize: "vertical",
    fontFamily: "inherit",
  };

  const infoCardStyle = {
    backgroundColor: colors.secondary,
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    border: `1px solid ${colors.border}`,
  };

  const infoGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
  };

  const infoItemStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };

  const infoLabelStyle = {
    fontSize: "12px",
    fontWeight: "500",
    color: colors.textLight,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  const infoValueStyle = {
    fontSize: "16px",
    fontWeight: "600",
    color: colors.text,
  };

  const IconUser = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );

  const IconSettings = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" />
    </svg>
  );

  const IconShield = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );

  const IconTruck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16,8 20,8 23,11 23,16 16,16" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );

  const renderInput = (label, name, type = "text", placeholder = "") => (
    <div style={inputGroupStyle}>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={formData[name]}
        onChange={(e) => handleInputChange(name, e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
        onFocus={(e) => (e.target.style.borderColor = colors.primary)}
        onBlur={(e) => (e.target.style.borderColor = colors.border)}
      />
    </div>
  );

  const renderTextarea = (label, name, placeholder = "") => (
    <div style={inputGroupStyle}>
      <label style={labelStyle}>{label}</label>
      <textarea
        value={formData[name]}
        onChange={(e) => handleInputChange(name, e.target.value)}
        placeholder={placeholder}
        style={textareaStyle}
        onFocus={(e) => (e.target.style.borderColor = colors.primary)}
        onBlur={(e) => (e.target.style.borderColor = colors.border)}
      />
    </div>
  );

  const onChange = (checked) => {
    setFormData((prev) => ({ ...prev, renewReferral: checked }));
  };

  const onSubmit = async () => {
    await dispatch(
      updateSettings({
        ...formData,
        eInvoiceRatePerPcs: Number(formData.eInvoiceRatePerPcs || 0),
        ewayRatePerPcs: Number(formData.ewayRatePerPcs || 0),
        gstPer: Number(formData.gstPer || 0),
        mobile: Number(formData.mobile || 0),
        id: setting?.id,
      })
    );
  };

  useEffect(() => {
    dispatch(getAllSettings());
  }, [dispatch]);

  useEffect(() => {
    if (setting) {
      setFormData({
        email: setting?.email,
        mobile: setting?.mobile,
        emailPassKey: setting?.emailPassKey,
        renewReferral: setting?.renewReferral,
        ewayRatePerPcs: setting?.ewayRatePerPcs,
        eInvoiceRatePerPcs: setting?.eInvoiceRatePerPcs,
        terms: setting?.terms,
        policy: setting?.policy,
        refundPolicy: setting?.refundPolicy,
        wpAuthToken: setting?.wpAuthToken,
        address: setting?.address,
        compName: setting?.compName,
        gstNo: setting?.gstNo,
        gstPer: setting?.gstPer,
        sac: setting?.sac,
      });
    }
  }, [setting]);

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: "1500px", margin: "5px 5px 5px 5px" }}>
        <div style={headerStyle}>Settings</div>
        <div style={subtitleStyle}>
          Manage your store configuration and preferences
        </div>

        {/* Tab Navigation */}
        {/* <div style={tabContainerStyle}>
          <button
            style={tabStyle(activeTab === "basic")}
            onClick={() => setActiveTab("basic")}
          >
            Basic Info
          </button>
          <button
            style={tabStyle(activeTab === "settings")}
            onClick={() => setActiveTab("settings")}
          >
            Configuration
          </button>
          <button
            style={tabStyle(activeTab === "legal")}
            onClick={() => setActiveTab("legal")}
          >
            Legal
          </button>
        </div> */}

        <div>
          {/* Basic Info Tab */}

          {/* Current Info Display */}
          {/* <div style={infoCardStyle}>
            <div style={{ ...cardHeaderStyle, marginBottom: "16px" }}>
              <IconUser />
              Current Information
            </div>
            <div style={infoGridStyle}>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Owner Name</div>
                <div style={infoValueStyle}>{formData.ownerName}</div>
              </div>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Email</div>
                <div style={infoValueStyle}>{formData.ownerEmail}</div>
              </div>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Contact</div>
                <div style={infoValueStyle}>{formData.ownerNo}</div>
              </div>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>WhatsApp</div>
                <div style={infoValueStyle}>{formData.ownerWpNo}</div>
              </div>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Order Amount</div>
                <div style={infoValueStyle}>₹{formData.orderAmt}</div>
              </div>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Return Period</div>
                <div style={infoValueStyle}>{formData.returnDay} days</div>
              </div>
            </div>
          </div> */}

          {/* Owner Details Form */}
          {/* <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <IconUser />
              Owner Details
            </div>
            <div style={gridStyle}>
              {renderInput(
                "Owner Name",
                "ownerName",
                "text",
                "Enter owner name"
              )}
              {renderInput(
                "Email Address",
                "ownerEmail",
                "email",
                "Enter email address"
              )}
              {renderInput(
                "Contact Number",
                "ownerNo",
                "tel",
                "Enter contact number"
              )}
              {renderInput(
                "WhatsApp Number",
                "ownerWpNo",
                "tel",
                "Enter WhatsApp number"
              )}
              {renderInput(
                "Return Period (Days)",
                "returnDay",
                "number",
                "Enter return days"
              )}
              {renderInput(
                "Minimum Order Amount",
                "orderAmt",
                "number",
                "Enter minimum amount"
              )}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button type="submit" style={buttonStyle()}>
                Update Owner Details
              </button>
            </div>
          </div> */}

          {/* Business Information */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <IconSettings />
              Business Information
            </div>
            <div style={gridStyle}>
              {renderInput("Email", "email", "text", "Enter email address")}
              {renderInput("Mobile", "mobile", "text", "Enter mobile number")}
              {renderInput(
                "Email Password",
                "emailPassKey",
                "text",
                "Enter Email password"
              )}
              {/* {renderInput(
                "Renew Referral",
                "renewReferral",
                "url",
                "Enter Renew Referral"
              )} */}

              <div style={inputGroupStyle}>
                <label style={labelStyle}>Renew Referral</label>
                <div>
                  <Switch
                    name="renewReferral"
                    checked={!!formData.renewReferral}
                    onChange={onChange}
                  />
                </div>
              </div>
              {renderInput(
                "Eway Rate Per Pcs",
                "ewayRatePerPcs",
                "number",
                "Enter Eway Rate Per Pcs"
              )}
              {renderInput(
                "Einvoice Rate Per Pcs",
                "eInvoiceRatePerPcs",
                "text",
                "Enter Einvoice Rate Per pcs"
              )}
              {renderInput(
                "Company Name",
                "compName",
                "text",
                "Enter Company Name"
              )}
              {renderInput(
                "GST No",
                "gstNo",
                "text",
                "Enter GST No"
              )}
              {renderInput(
                "GST %",
                "gstPer",
                "number",
                "Enter GST %"
              )}
              {renderInput(
                "SAC Code",
                "sac",
                "text",
                "Enter SAC Code"
              )}
              <div style={inputGroupStyle2}>
                <label style={labelStyle}>Wp Auth Token</label>
                <input
                  type="text"
                  value={formData?.wpAuthToken}
                  onChange={(e) => handleInputChange('wpAuthToken', e.target.value)}
                  placeholder="Enter Wp Auth Token"
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = colors.primary)}
                  onBlur={(e) => (e.target.style.borderColor = colors.border)}
                />
              </div>

              <div style={inputGroupStyle3}>
                <label style={labelStyle}>Address</label>
                <textarea
                  value={formData?.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter Address"
                  style={textareaStyle}
                  onFocus={(e) => (e.target.style.borderColor = colors.primary)}
                  onBlur={(e) => (e.target.style.borderColor = colors.border)}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                type="submit"
                style={buttonStyle()}
                onClick={() => onSubmit()}
              >
                Update Business Info
              </button>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <IconShield />
              Terms and Conditions
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Terms and Conditions</label>
              <ReactQuill
                value={formData.terms}
                onChange={(content) =>
                  setFormData((prev) => ({ ...prev, terms: content }))
                }
                placeholder="Enter your terms and conditions here..."
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                type="submit"
                style={buttonStyle()}
                onClick={() => onSubmit()}
              >
                Update Terms & Conditions
              </button>
            </div>
          </div>

          {/* Privacy Policy */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <IconShield />
              Privacy Policy
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Privacy Policy</label>
              <ReactQuill
                value={formData.policy}
                onChange={(content) =>
                  setFormData((prev) => ({ ...prev, policy: content }))
                }
                placeholder="Enter your privacy policy here..."
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                type="submit"
                style={buttonStyle()}
                onClick={() => onSubmit()}
              >
                Update Privacy Policy
              </button>
            </div>
          </div>

          {/* Privacy Policy */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <IconShield />
              Refund Policy
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Privacy Policy</label>
              <ReactQuill
                value={formData.refundPolicy}
                onChange={(content) =>
                  setFormData((prev) => ({ ...prev, refundPolicy: content }))
                }
                placeholder="Enter your privacy policy here..."
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                type="submit"
                style={buttonStyle()}
                onClick={() => onSubmit()}
              >
                Update Refund Policy
              </button>
            </div>
          </div>

          {/* Settings Tab */}
          {/* {activeTab === "settings" && (
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <IconTruck />
                Delivery Configuration
              </div>
              <div style={gridStyle}>
                {renderInput(
                  "Surat City Delivery",
                  "suratCity",
                  "number",
                  "Enter delivery charge"
                )}
                {renderInput(
                  "Gujarat State Delivery",
                  "stateGujarat",
                  "number",
                  "Enter delivery charge"
                )}
                {renderInput(
                  "India Delivery",
                  "countryIndia",
                  "number",
                  "Enter delivery charge"
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "24px",
                }}
              >
                <button type="submit" style={buttonStyle()}>
                  Update Delivery Settings
                </button>
              </div>
            </div>
          )} */}

          {/* Legal Tab */}
          {activeTab === "legal" && (
            <>
              {/* Terms and Conditions */}
              <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <IconShield />
                  Terms and Conditions
                </div>
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Terms and Conditions</label>
                  <ReactQuill
                    value={formData.termsCond}
                    onChange={(content) =>
                      handleInputChange("termsCond", content)
                    }
                    placeholder="Enter your terms and conditions here..."
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "24px",
                  }}
                >
                  <button type="submit" style={buttonStyle()}>
                    Update Terms & Conditions
                  </button>
                </div>
              </div>

              {/* Privacy Policy */}
              <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <IconShield />
                  Privacy Policy
                </div>
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Privacy Policy</label>
                  <ReactQuill
                    value={formData.privacyPolicy}
                    onChange={(content) =>
                      handleInputChange("privacyPolicy", content)
                    }
                    placeholder="Enter your privacy policy here..."
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "24px",
                  }}
                >
                  <button type="submit" style={buttonStyle()}>
                    Update Privacy Policy
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModernSettingsPage;
